/**
 * Shared "read notifications" store. The admin notification feed is derived
 * from pending action queues (no server read-state), so read/unread is tracked
 * client-side. Both the Topbar bell badge and the Notifications page subscribe
 * here, so marking something read anywhere updates the badge live.
 */
import { useSyncExternalStore } from "react";

const READ_KEY = "txprime:notifications:read";

function load(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
}

let current = load();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Sync across tabs.
  const onStorage = (e: StorageEvent) => { if (e.key === READ_KEY) { current = load(); cb(); } };
  window.addEventListener("storage", onStorage);
  return () => { listeners.delete(cb); window.removeEventListener("storage", onStorage); };
}

function getSnapshot(): Set<string> {
  return current;
}

/** Mark one or more notification ids as read. */
export function markNotificationsRead(ids: string[]): void {
  const next = new Set(current);
  let changed = false;
  for (const id of ids) if (!next.has(id)) { next.add(id); changed = true; }
  if (!changed) return;
  current = next;
  try { localStorage.setItem(READ_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
  emit();
}

/** Reactive set of read notification ids. */
export function useReadNotifications(): Set<string> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
